import React, { useState, useMemo } from 'react';
import { useGame } from '../context/GameContext';
import GameCard from '../components/GameCard';
import { CARDS, cardRarity } from '../game/cards';
import { ArrowLeft } from 'lucide-react';

const RARITIES = ['all', 'common', 'uncommon', 'rare', 'legendary'];
const TYPES = ['all', 'attack', 'skill', 'power', 'curse', 'status'];
const SORTS = ['order', 'cost', 'name', 'rarity', 'type'];

// v2.3 QoL: gather all tags used across a deck for a filter dropdown
function collectTags(deck) {
  const tags = new Set();
  for (const cid of deck) {
    const c = CARDS[cid];
    if (c?.tags) c.tags.forEach(t => tags.add(t));
  }
  return ['all', ...Array.from(tags).sort()];
}

export default function DeckView() {
  const { run, setScreen } = useGame();
  const [rarityFilter, setRarityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all'); // v2.3
  const [sortMode, setSortMode] = useState('order');
  const deckIndexed = useMemo(() => (run?.player?.deck || []).map((cid, i) => ({ cid, i, card: CARDS[cid] || {} })), [run?.player?.deck]);
  const allTags = useMemo(() => collectTags(run?.player?.deck || []), [run?.player?.deck]);
  if (!run) return null;

  const filtered = deckIndexed.filter(x => {
    const r = cardRarity(x.cid);
    if (rarityFilter !== 'all' && r !== rarityFilter) return false;
    if (typeFilter !== 'all' && (x.card.type || '').toLowerCase() !== typeFilter) return false;
    if (tagFilter !== 'all' && !(x.card.tags || []).includes(tagFilter)) return false;
    return true;
  });
  const RARITY_ORDER = { common: 0, uncommon: 1, rare: 2, legendary: 3 };
  const sorted = [...filtered].sort((a, b) => {
    if (sortMode === 'order') return a.i - b.i;
    if (sortMode === 'cost') return (a.card.cost === 'X' ? -1 : a.card.cost || 0) - (b.card.cost === 'X' ? -1 : b.card.cost || 0);
    if (sortMode === 'name') return (a.card.name || a.cid).localeCompare(b.card.name || b.cid);
    if (sortMode === 'rarity') return (RARITY_ORDER[cardRarity(b.cid)]||0) - (RARITY_ORDER[cardRarity(a.cid)]||0);
    if (sortMode === 'type') return (a.card.type || '').localeCompare(b.card.type || '');
    return 0;
  });

  return (
    <div className="min-h-screen w-full px-6 py-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button className="brutal-btn text-sm" onClick={() => setScreen('map')} data-testid="deckview-back-button">
          <ArrowLeft size={16} /> BACK
        </button>
        <h1 className="font-display text-4xl font-black uppercase text-[color:var(--neon)]">YOUR DECK</h1>
        <div className="text-sm font-mono uppercase text-[color:var(--text-dim)]" data-testid="deck-count">{run.player.deck.length} CARDS</div>
      </div>

      {/* v1.4 QoL: sort + filter controls */}
      <div className="mt-4 max-w-3xl mx-auto brutal-border-dim p-3 bg-[#0a0a0a] flex flex-wrap items-center gap-3 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-[color:var(--text-dim)] uppercase">SORT</span>
          <select className="bg-[#050505] border-2 border-[color:var(--neon)] px-2 py-1 text-white font-mono uppercase"
            value={sortMode} onChange={e => setSortMode(e.target.value)} data-testid="deck-sort">
            {SORTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[color:var(--text-dim)] uppercase">RARITY</span>
          <select className="bg-[#050505] border-2 border-[color:var(--text-dim)] px-2 py-1 text-white font-mono uppercase"
            value={rarityFilter} onChange={e => setRarityFilter(e.target.value)} data-testid="deck-filter-rarity">
            {RARITIES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[color:var(--text-dim)] uppercase">TYPE</span>
          <select className="bg-[#050505] border-2 border-[color:var(--text-dim)] px-2 py-1 text-white font-mono uppercase"
            value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="deck-filter-type">
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {/* v2.3 QoL: tag filter (only shows if deck has any tagged cards) */}
        {allTags.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-[color:var(--text-dim)] uppercase">TAG</span>
            <select className="bg-[#050505] border-2 border-[color:var(--pink)] px-2 py-1 text-white font-mono uppercase"
              value={tagFilter} onChange={e => setTagFilter(e.target.value)} data-testid="deck-filter-tag">
              {allTags.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}
        <div className="ml-auto uppercase text-[color:var(--text-dim)] font-mono" data-testid="deck-shown-count">
          {sorted.length}/{deckIndexed.length}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-4 justify-center" data-testid="deck-cards-list">
        {sorted.map((x, k) => (
          <GameCard key={`${x.i}-${x.cid}`} cardId={x.cid} testid={`deck-card-${k}`} />
        ))}
        {sorted.length === 0 && (
          <div className="text-center py-16 text-[color:var(--text-dim)] uppercase tracking-widest">No cards match your filters.</div>
        )}
      </div>
    </div>
  );
}
